import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Globe, Users, Loader2, Image, Eye, User, Bookmark, Search, SlidersHorizontal, X, MoreVertical, Edit2, EyeOff, Trash2, Calendar } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import FollowButton from '../components/FollowButton';
import BookmarkButton from '../components/BookmarkButton';
import ScrollableTabs from '../components/ScrollableTabs';
import ConfirmDialog from '../components/ConfirmDialog';
import { BACKEND_URL } from '../config/api';

// Module-level cache — survives component unmount, resets on full page reload
const feedCache = { following: null, latest: null, bookmarks: null, scrollY: 0, tab: null };

export default function CommunityFeedPage({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = searchParams.get('tab');
  const initialTab = paramTab === 'latest' ? 'latest' : paramTab === 'following' ? 'following' : paramTab === 'bookmarks' ? 'bookmarks' : (feedCache.tab || 'latest');

  const [activeTab, setActiveTab] = useState(initialTab);
  const cached = feedCache[activeTab];
  const [designs, setDesigns] = useState(cached?.designs || []);
  const [total, setTotal] = useState(cached?.total || 0);
  const [hasMore, setHasMore] = useState(cached?.hasMore || false);
  const [offset, setOffset] = useState(cached?.offset || 0);
  const [loading, setLoading] = useState(!cached);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const needsRestore = useRef(!!cached);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  
  // Creator suggestions
  const [creatorSuggestions, setCreatorSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const suggestionDebounceRef = useRef(null);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ techniques: [], occasions: [] });
  const [selectedTechnique, setSelectedTechnique] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState('');
  
  // Owner action menu state
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/designs/filter-options`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setFilterOptions({
            techniques: data.techniques || [],
            // Use all_occasions for filter dropdown (full list, not just existing in DB)
            occasions: data.all_occasions || data.occasions || [],
          });
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Restore scroll synchronously before paint when returning from cache
  useLayoutEffect(() => {
    if (needsRestore.current && feedCache.scrollY > 0) {
      window.scrollTo(0, feedCache.scrollY);
      feedCache.scrollY = 0;
      needsRestore.current = false;
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch creator suggestions when @ is typed
  const fetchCreatorSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setCreatorSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/feed/creators/suggest?q=${encodeURIComponent(query)}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setCreatorSuggestions(data.creators || []);
        setShowSuggestions(data.creators?.length > 0);
      }
    } catch (err) {
      console.error('Failed to fetch creator suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Check for @ prefix for creator suggestions
    if (value.startsWith('@') && value.length > 1) {
      // Debounce creator suggestions
      clearTimeout(suggestionDebounceRef.current);
      suggestionDebounceRef.current = setTimeout(() => {
        fetchCreatorSuggestions(value.substring(1));
      }, 200);
    } else {
      setShowSuggestions(false);
      setCreatorSuggestions([]);
    }
    
    // Debounce search
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // Trigger search
      if (activeTab === 'latest') {
        fetchFeed('latest', true, value, sortBy, selectedOccasion, selectedTechnique);
      }
    }, 400);
  };

  // Handle selecting a creator suggestion
  const selectCreator = (handle) => {
    setSearchQuery(`@${handle}`);
    setShowSuggestions(false);
    // Trigger search immediately
    if (activeTab === 'latest') {
      fetchFeed('latest', true, `@${handle}`, sortBy, selectedOccasion, selectedTechnique);
    }
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    if (activeTab === 'latest') {
      fetchFeed('latest', true, searchQuery, newSort, selectedOccasion, selectedTechnique);
    }
  };

  // Handle filter changes
  const handleOccasionChange = (occ) => {
    setSelectedOccasion(occ);
    if (activeTab === 'latest') {
      fetchFeed('latest', true, searchQuery, sortBy, occ, selectedTechnique);
    }
  };

  const handleTechniqueChange = (tech) => {
    setSelectedTechnique(tech);
    if (activeTab === 'latest') {
      fetchFeed('latest', true, searchQuery, sortBy, selectedOccasion, tech);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSortBy('recent');
    setSelectedOccasion('');
    setSelectedTechnique('');
    setShowSuggestions(false);
    if (activeTab === 'latest') {
      fetchFeed('latest', true, '', 'recent', '', '');
    }
  };

  // Owner action handlers
  const handleDeleteClick = (design) => {
    setDeleteTarget(design);
    setActionMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${BACKEND_URL}/api/designs/${deleteTarget.design_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      // Remove from local state
      setDesigns((prev) => prev.filter((d) => d.design_id !== deleteTarget.design_id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to delete design:', err);
    }
    setDeleteTarget(null);
  };

  const handleTogglePublish = async (design) => {
    const endpoint = design.is_public ? 'unpublish' : 'publish';
    try {
      const res = await fetch(`${BACKEND_URL}/api/designs/${design.design_id}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        // If unpublishing, remove from feed (since feed shows public designs)
        if (design.is_public) {
          setDesigns((prev) => prev.filter((d) => d.design_id !== design.design_id));
          setTotal((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      console.error('Failed to toggle publish:', err);
    }
    setActionMenu(null);
  };

  const fetchFeed = useCallback(async (tab, reset = false, searchTerm = '', sort = 'recent', occasion = '', technique = '') => {
    const currentOffset = reset ? 0 : offset;
    setLoading(true);
    try {
      let res;
      if (tab === 'bookmarks') {
        res = await fetch(`${BACKEND_URL}/api/bookmarks?limit=20&skip=${currentOffset}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
      } else if (tab === 'following') {
        res = await fetch(`${BACKEND_URL}/api/feed/following?limit=20&offset=${currentOffset}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
      } else {
        // Latest tab with search, sort, and filters
        let url = `${BACKEND_URL}/api/feed/latest?limit=20&offset=${currentOffset}&sort=${sort}`;
        if (searchTerm && searchTerm.trim()) {
          url += `&q=${encodeURIComponent(searchTerm.trim())}`;
        }
        if (occasion && occasion !== 'all') {
          url += `&occasion=${encodeURIComponent(occasion)}`;
        }
        if (technique && technique !== 'all') {
          url += `&technique=${encodeURIComponent(technique)}`;
        }
        res = await fetch(url, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
      }
      
      if (res.ok) {
        const data = await res.json();
        const newDesigns = reset ? (data.designs || []) : [...designs, ...(data.designs || [])];
        const newTotal = data.total || 0;
        const newHasMore = data.has_more || false;
        const newOffset = reset ? 20 : currentOffset + 20;

        setDesigns(newDesigns);
        setTotal(newTotal);
        setHasMore(newHasMore);
        setOffset(newOffset);

        // Update cache
        feedCache[tab] = { designs: newDesigns, total: newTotal, hasMore: newHasMore, offset: newOffset };
        
        // For non-bookmark tabs, fetch bookmark status for displayed designs
        if (tab !== 'bookmarks' && newDesigns.length > 0) {
          const designIds = newDesigns.map(d => d.design_id).join(',');
          const bookmarkRes = await fetch(`${BACKEND_URL}/api/bookmarks/check-multiple?design_ids=${designIds}`, {
            credentials: 'include',
            headers: getAuthHeaders(),
          });
          if (bookmarkRes.ok) {
            const bookmarkData = await bookmarkRes.json();
            setBookmarkedIds(new Set(bookmarkData.bookmarked || []));
          }
        } else if (tab === 'bookmarks') {
          // All bookmarks are bookmarked by definition
          setBookmarkedIds(new Set(newDesigns.map(d => d.design_id)));
        }
      }
    } catch (err) {
      console.error('Failed to fetch feed:', err);
    } finally {
      setLoading(false);
    }
  }, [offset, designs]);

  // Fetch on mount (only if no cache) or when tab changes
  useEffect(() => {
    const existing = feedCache[activeTab];
    if (existing) {
      setDesigns(existing.designs);
      setTotal(existing.total);
      setHasMore(existing.hasMore);
      setOffset(existing.offset);
      setLoading(false);
    } else {
      fetchFeed(activeTab, true);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
    feedCache.tab = tab;
    feedCache.scrollY = 0;
    window.scrollTo(0, 0);
    // Clear search when switching tabs
    setSearchQuery('');
    setShowFilters(false);

    // If no cache for this tab, it will fetch in the effect above
    const existing = feedCache[tab];
    if (!existing) {
      setDesigns([]);
      setTotal(0);
      setHasMore(false);
      setOffset(0);
    }
  };

  const navigateToDesign = (designId) => {
    feedCache.scrollY = window.scrollY;
    feedCache.tab = activeTab;
    navigate(`/designs/${designId}`, { state: { from: 'explore', tab: activeTab } });
  };

  return (
    <div className="min-h-screen bg-cream pb-20 lg:pb-0">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-3 pb-6 sm:py-6">
        {/* Header - Compact */}
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <h1 className="text-lg sm:text-2xl font-light text-charcoal" data-testid="community-feed-title">Explore</h1>
          </div>
        </div>

        {/* Search Bar with Filter Toggle */}
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-light" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={activeTab === 'latest' ? "Search designs or @creator..." : "Search..."}
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => {
                if (searchQuery.startsWith('@') && creatorSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              disabled={activeTab !== 'latest'}
              className={`w-full pl-10 pr-4 py-2.5 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all bg-white text-sm ${
                activeTab !== 'latest' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="explore-search-input"
            />
            {/* Clear search button */}
            {searchQuery && activeTab === 'latest' && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowSuggestions(false);
                  fetchFeed('latest', true, '', sortBy);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-light hover:text-charcoal"
              >
                <X size={16} />
              </button>
            )}
            {/* Creator suggestions dropdown */}
            {showSuggestions && creatorSuggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-beige rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
              >
                {loadingSuggestions ? (
                  <div className="p-3 text-center text-gray-soft">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Loading...
                  </div>
                ) : (
                  creatorSuggestions.map((creator) => (
                    <button
                      key={creator.user_id}
                      onClick={() => selectCreator(creator.handle)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-cream transition-colors text-left"
                    >
                      {creator.picture ? (
                        <img src={creator.picture} alt={creator.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center">
                          <User size={14} className="text-sage" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-charcoal">{creator.name}</p>
                        <p className="text-xs text-sage">@{creator.handle}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-lg border transition-all ${
              showFilters || sortBy !== 'recent' || selectedOccasion || selectedTechnique
                ? 'border-sage bg-sage/10 text-sage'
                : 'border-beige bg-white text-gray-soft hover:text-charcoal'
            }`}
            title="Filters"
            data-testid="explore-filter-toggle-btn"
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-4 p-2 sm:p-3 bg-white rounded-lg border border-beige">
            {/* Sort */}
            <div className="flex-1 min-w-[120px]">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                disabled={activeTab !== 'latest'}
                className={`w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white text-charcoal text-sm ${
                  activeTab !== 'latest' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="explore-sort-select"
              >
                <option value="recent">Sort: Recent</option>
                <option value="popular">Sort: Popular</option>
                <option value="name">Sort: Name (A-Z)</option>
              </select>
            </div>
            
            {/* Occasion Filter */}
            <div className="flex-1 min-w-[120px]">
              <select
                value={selectedOccasion}
                onChange={(e) => handleOccasionChange(e.target.value)}
                disabled={activeTab !== 'latest'}
                className={`w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white text-charcoal text-sm ${
                  activeTab !== 'latest' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="explore-occasion-select"
              >
                <option value="">Occasion: All</option>
                {filterOptions.occasions.map((occ) => (
                  <option key={occ} value={occ}>
                    {occ.charAt(0).toUpperCase() + occ.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Technique Filter */}
            <div className="flex-1 min-w-[120px]">
              <select
                value={selectedTechnique}
                onChange={(e) => handleTechniqueChange(e.target.value)}
                disabled={activeTab !== 'latest'}
                className={`w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white text-charcoal text-sm ${
                  activeTab !== 'latest' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="explore-technique-select"
              >
                <option value="">Technique: All</option>
                {filterOptions.techniques.map((tech) => (
                  <option key={tech} value={tech}>
                    {tech.charAt(0).toUpperCase() + tech.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Clear Filters Button - show if any filter is active */}
            {(selectedOccasion || selectedTechnique || sortBy !== 'recent' || searchQuery) && activeTab === 'latest' && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-rose hover:text-rose-dark hover:bg-rose/5 rounded-lg transition-colors"
                data-testid="clear-filters-btn"
              >
                Clear All
              </button>
            )}
            
            {activeTab !== 'latest' && (
              <p className="text-xs text-gray-soft w-full mt-1">Filters available on Latest tab only</p>
            )}
          </div>
        )}

        {/* Tabs */}
        <ScrollableTabs className="mb-2 sm:mb-4 border-b border-beige" data-testid="feed-tabs">
          <button
            onClick={() => switchTab('latest')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'latest'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
            data-testid="tab-latest"
          >
            <span className="flex items-center gap-2">
              <Globe size={16} />
              Latest
            </span>
            {activeTab === 'latest' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
          <button
            onClick={() => switchTab('following')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'following'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
            data-testid="tab-following"
          >
            <span className="flex items-center gap-2">
              <Users size={16} />
              Following
            </span>
            {activeTab === 'following' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
          <button
            onClick={() => switchTab('bookmarks')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'bookmarks'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
            data-testid="tab-bookmarks"
          >
            <span className="flex items-center gap-2">
              <Bookmark size={16} />
              Bookmarks
            </span>
            {activeTab === 'bookmarks' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
        </ScrollableTabs>

        {/* Content */}
        <div className="mt-6">
        {loading && designs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-sage" size={32} />
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-beige" data-testid="empty-feed">
            {activeTab === 'following' ? (
              <>
                <Users size={48} className="mx-auto text-beige mb-4" />
                <h3 className="text-lg font-medium text-charcoal mb-2">No designs from people you follow</h3>
                <p className="text-sm text-gray-soft mb-6 max-w-md mx-auto">
                  Follow other crafters to see their public designs here. Or check out the Latest tab to discover new creators.
                </p>
                <button
                  onClick={() => switchTab('latest')}
                  className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm"
                >
                  Browse Latest
                </button>
              </>
            ) : activeTab === 'bookmarks' ? (
              <>
                <Bookmark size={48} className="mx-auto text-beige mb-4" />
                <h3 className="text-lg font-medium text-charcoal mb-2">No bookmarks yet</h3>
                <p className="text-sm text-gray-soft mb-6 max-w-md mx-auto">
                  Save designs you love by clicking the bookmark icon. They'll appear here for easy access.
                </p>
                <button
                  onClick={() => switchTab('latest')}
                  className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm"
                >
                  Explore Designs
                </button>
              </>
            ) : (
              <>
                <Image size={48} className="mx-auto text-beige mb-4" />
                <h3 className="text-lg font-medium text-charcoal mb-2">No public designs yet</h3>
                <p className="text-sm text-gray-soft max-w-md mx-auto">
                  Be the first to share your creations with the community!
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {designs.map((design) => (
                <div
                  key={design.design_id}
                  className="bg-white rounded-xl border border-beige overflow-hidden group hover:shadow-md transition-shadow"
                  data-testid={`feed-card-${design.design_id}`}
                >
                  {/* Image */}
                  <div
                    className="aspect-square bg-cream relative cursor-pointer"
                    onClick={() => navigateToDesign(design.design_id)}
                  >
                    {design.image_url ? (
                      <img src={design.image_url} alt={design.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image size={48} className="text-beige" />
                      </div>
                    )}
                    {/* Owner 3-dot menu - show for owned designs */}
                    {(design.user_id === user?.user_id || design.creator?.user_id === user?.user_id) && (
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === design.design_id ? null : design.design_id); }}
                          className="p-1.5 bg-white/80 backdrop-blur-sm text-gray-soft hover:text-charcoal rounded-full shadow-sm transition-colors"
                          data-testid={`feed-design-menu-${design.design_id}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {actionMenu === design.design_id && (
                          <div className="absolute right-0 top-9 bg-white border border-beige rounded-lg shadow-lg py-1 w-40" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => { setActionMenu(null); navigate(`/my-designs/${design.design_id}?edit=true`, { state: { from: 'explore', tab: activeTab } }); }}
                              className="w-full text-left px-3 py-2 text-sm text-charcoal hover:bg-cream flex items-center gap-2"
                              data-testid={`feed-design-edit-${design.design_id}`}
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleTogglePublish(design)}
                              className="w-full text-left px-3 py-2 text-sm text-charcoal hover:bg-cream flex items-center gap-2"
                              data-testid={`feed-design-toggle-${design.design_id}`}
                            >
                              <EyeOff size={14} />
                              Make Private
                            </button>
                            <button
                              onClick={() => handleDeleteClick(design)}
                              className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                              data-testid={`feed-design-delete-${design.design_id}`}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Bookmark button - only show for designs not owned by current user */}
                    {design.user_id !== user?.user_id && design.creator?.user_id !== user?.user_id && (
                      <div className="absolute top-2 right-2">
                        <BookmarkButton 
                          designId={design.design_id} 
                          initialBookmarked={bookmarkedIds.has(design.design_id)}
                          size="small"
                          onToggle={(isBookmarked) => {
                            if (isBookmarked) {
                              setBookmarkedIds(prev => new Set([...prev, design.design_id]));
                            } else {
                              setBookmarkedIds(prev => {
                                const next = new Set(prev);
                                next.delete(design.design_id);
                                return next;
                              });
                              // If on bookmarks tab, remove from list
                              if (activeTab === 'bookmarks') {
                                setDesigns(prev => prev.filter(d => d.design_id !== design.design_id));
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                    {design.view_count > 0 && (
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-charcoal/40 text-white rounded-full text-xs flex items-center gap-1">
                        <Eye size={10} />
                        {design.view_count}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3
                      className="font-medium text-charcoal truncate mb-1 cursor-pointer hover:text-sage transition-colors"
                      onClick={() => navigateToDesign(design.design_id)}
                    >
                      {design.title}
                    </h3>
                    
                    {/* Occasion badge */}
                    {design.occasion && design.occasion !== 'none' && (
                      <div className="flex items-center gap-1 mb-2">
                        <Calendar size={11} className="text-rose" />
                        <span className="text-xs text-rose-dark capitalize">{design.occasion}</span>
                      </div>
                    )}

                    {/* Creator + Follow */}
                    {design.creator && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {design.creator.picture ? (
                            <img src={design.creator.picture} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-sage-light flex items-center justify-center flex-shrink-0">
                              <User size={10} className="text-sage" />
                            </div>
                          )}
                          <span className="text-xs text-gray-soft truncate">
                            {design.creator.handle ? `@${design.creator.handle}` : design.creator.name}
                          </span>
                        </div>
                        {design.creator.user_id !== user?.user_id && (
                          <FollowButton targetUserId={design.creator.user_id} size="small" />
                        )}
                      </div>
                    )}

                    {/* Tags */}
                    {design.technique_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {design.technique_tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-sage/10 text-sage-dark text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                        {design.technique_tags.length > 4 && (
                          <span className="px-2 py-0.5 text-xs text-gray-soft">
                            +{design.technique_tags.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={() => fetchFeed(activeTab)}
                  disabled={loading}
                  className="px-6 py-2.5 border border-beige text-charcoal rounded-lg hover:bg-white transition-colors text-sm"
                  data-testid="load-more-btn"
                >
                  {loading ? <Loader2 size={16} className="animate-spin inline" /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Design"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}
